import React, {useMemo, useState, useEffect} from 'react';
import {Box, Select, TextInput, Button} from 'grommet';
import BaseFilter from './BaseFilter';
import CurrencyInput from "../CurrencyInput";
import { convertDollarToCent, convertCentToDollar, formatCurrency } from "../../utils/numbers";
import {useSession} from "next-auth/react";

// Helper function to strip commas from input values
const stripCommas = (value) => {
    return typeof value === 'string' ? value.replace(/,/g, '') : value;
};

// Helper function to swap min and max if max is smaller than min
const swapMinMaxIfNeeded = (min, max) => {
    const minNum = parseFloat(min);
    const maxNum = parseFloat(max);

    // Only swap if both values are valid numbers and max < min
    if (!isNaN(minNum) && !isNaN(maxNum) && maxNum < minNum) {
        return { min: max, max: min };
    }

    return { min, max };
};

// Helper to determine which option to select based on amount range values
const getOptionFromRange = (min, max) => {
    const hasMin = min !== undefined && min !== null && min !== '';
    const hasMax = max !== undefined && max !== null && max !== '';
    if (!hasMin && !hasMax) return '';
    if (hasMin && hasMax && min === max) return 'is equal to';
    if (hasMin && hasMax) return 'is between';
    if (hasMin && !hasMax) return 'is greater than';
    if (!hasMin && hasMax) return 'is less than';
    return '';
};

const amountOptions = [
    'is between',
    'is equal to',
    'is greater than',
    'is less than'
];

const AmountFilter = ({ filterName = "Amount", selectedAmountRange , onApplyFilter }) => {
    const {data: session} = useSession();
    const currency = session?.company?.currency || 'USD';
    const initialAmountRange = useMemo(() => {
        // selectedAmountRange values are in cents, convert to dollars for display
        return selectedAmountRange
            ? {
                min: selectedAmountRange[0] ? convertCentToDollar(selectedAmountRange[0]) : '',
                max: selectedAmountRange[1] ? convertCentToDollar(selectedAmountRange[1]) : ''
            }
            : { min: '', max: '' };
    }, [selectedAmountRange]);

    const [selectedOption, setSelectedOption] = useState(() =>
        getOptionFromRange(selectedAmountRange?.[0], selectedAmountRange?.[1])
    );
    const [amount, setAmount] = useState(initialAmountRange);

    // Sync state when selectedAmountRange prop changes (e.g., from URL on navigation back)
    useEffect(() => {
        // Only sync if we have actual values - check for truthy values (not 0, '', undefined, null)
        const hasMin = selectedAmountRange?.[0] !== undefined && selectedAmountRange?.[0] !== '' && selectedAmountRange?.[0] !== null;
        const hasMax = selectedAmountRange?.[1] !== undefined && selectedAmountRange?.[1] !== '' && selectedAmountRange?.[1] !== null;
        if (!hasMin && !hasMax) return;

        // selectedAmountRange values are in cents, convert to dollars for display
        const min = hasMin ? convertCentToDollar(selectedAmountRange[0]) : '';
        const max = hasMax ? convertCentToDollar(selectedAmountRange[1]) : '';
        setAmount({ min, max });
        setSelectedOption(getOptionFromRange(selectedAmountRange?.[0], selectedAmountRange?.[1]));
    }, [selectedAmountRange]);

    const renderContent = () => {
        const renderAmountInputs = () => {
            switch (selectedOption) {
            case 'is between':
                return (
                    <Box gap="xsmall">
                        <TextInput
                            placeholder="0.00"
                            value={amount.min}
                            size="small"
                            onChange={(e) => setAmount({ ...amount, min: stripCommas(e.target.value) })}
                            component={CurrencyInput}
                        />
                        <TextInput
                            placeholder="0.00"
                            value={amount.max}
                            size="small"
                            onChange={(e) => setAmount({ ...amount, max: stripCommas(e.target.value) })}
                            component={CurrencyInput}
                        />
                    </Box>
                );
            case 'is equal to':
                return (
                    <TextInput
                        placeholder="0.00"
                        value={amount.min}
                        size="small"
                        onChange={(e) => setAmount({ ...amount, max: stripCommas(e.target.value), min: stripCommas(e.target.value) }) }
                        component={CurrencyInput}
                    />
                );
            case 'is greater than':
                return (
                    <TextInput
                        placeholder="0.00"
                        value={amount.min}
                        size="small"
                        onChange={(e) => setAmount({ ...amount, min: stripCommas(e.target.value), max: '' }) }
                        component={CurrencyInput}
                    />
                );
            case 'is less than':
                return (
                    <TextInput
                        placeholder="0.00"
                        value={amount.max}
                        size="small"
                        onChange={(e) => setAmount({ ...amount, min: '', max: stripCommas(e.target.value) }) }
                        component={CurrencyInput}
                    />
                );
            default:
                return null;
            }
        };

        return (
            <Box gap="xsmall" responsive={false}>
                <Select
                    options={amountOptions}
                    value={selectedOption}
                    size="small"
                    onChange={({ option }) => setSelectedOption(option)}
                    placeholder="Select filter type"
                />
                <Box gap="xsmall" responsive={false}>
                    {renderAmountInputs()}
                    <Button fill type="submit" primary label="Apply"/>
                </Box>
            </Box>
        );
    };

    const renderButtonLabel = () => {
        if (selectedOption === '') { return null }

        const min = formatCurrency(convertDollarToCent(amount.min),currency)
        const max = formatCurrency(convertDollarToCent(amount.max),currency)

        if (selectedOption === amountOptions[0]) return `${min} to ${max}`;
        if (selectedOption === amountOptions[1]) return `Exactly ${min}`;
        if (selectedOption === amountOptions[2]) return `More than ${min}`;
        if (selectedOption === amountOptions[3]) return `Less than ${max}`;
    }

    const onApply = () => {
        if (selectedOption === '') { onReset(); }
        let {min, max} = amount;

        // Swap min and max if needed for "is between" option
        if (selectedOption === 'is between') {
            const swapped = swapMinMaxIfNeeded(min, max);
            min = swapped.min;
            max = swapped.max;
        }

        onApplyFilter({
            min: min && convertDollarToCent(min) || '',
            max: max && convertDollarToCent(max) || ''
        });
    }

    const onReset = () => {
        setSelectedOption('');
        setAmount({min: '', max: ''});
        onApplyFilter(undefined);
    }

    return (
        <BaseFilter
            filterName={filterName}
            renderContent={renderContent}
            renderButtonLabel={renderButtonLabel}
            isActive={selectedOption !== ''}
            onApply={onApply}
            onReset={onReset}
        />
    );
};

export default AmountFilter;
